import api from './api';

export interface Marque {
  id: number;
  nom: string;
  description?: string;
  logo?: string;
  date_creation: string;
}

export interface Categorie {
  id: number;
  nom: string;
  description?: string;
  icone?: string;
  date_creation: string;
}

export interface Produit {
  id: number;
  reference: string;
  nom: string;
  description: string;
  prix: number;
  base_price: number;
  quantite_stock: number;
  seuil_alerte: number;
  marque_id: number;
  categorie_id: number;
  actif: boolean;
  offer_id: number | null;
  images: string[];
  specifications: Record<string, any>;
  date_creation: string;
  date_mise_a_jour: string;
  marque: Marque;
  categorie: Categorie;
}

export interface FiltresProduits {
  recherche?: string;
  marque_id?: number;
  categorie_id?: number;
  actif?: boolean;
  prix_min?: number;
  prix_max?: number;
  en_stock?: boolean;
  page?: number;
  limit?: number;
  tri?: string;
  ordre?: 'asc' | 'desc';
}

interface ReponsePaginee<T> {
  success: boolean;
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface ReponseProduit {
  success: boolean;
  data: Produit;
}

export interface MiseAJourProduit extends Partial<CreationProduit> {
  actif?: boolean;
}

export interface CreationProduit {
  reference: string;
  nom: string;
  description: string;
  prix: number;
  cout: number;
  quantite_stock: number;
  seuil_alerte: number;
  marque_id: number;
  categorie_id: number;
  images: string[];
  specifications: Record<string, any>;
}

class ServiceProduits {
  /**
   * Récupère la liste des produits avec pagination et filtres
   */
  async listerProduits(filtres: FiltresProduits = {}): Promise<ReponsePaginee<Produit>> {
    try {
      const response = await api.get<ReponsePaginee<Produit>>('/products', { params: filtres });
      return response.data;
    } catch (erreur) {
      console.error('Erreur lors de la récupération des produits:', erreur);
      throw new Error('Impossible de charger la liste des produits.');
    }
  }

  /**
   * Récupère un produit par son ID
   */
  async obtenirProduit(id: number): Promise<Produit> {
    try {
      const response = await api.get<ReponseProduit>(`/products/${id}`);
      return response.data.data;
    } catch (erreur) {
      console.error(`Erreur lors de la récupération du produit ${id}:`, erreur);
      throw new Error('Produit non trouvé.');
    }
  }

  /**
   * Crée un nouveau produit
   */
  async creerProduit(donnees: CreationProduit): Promise<Produit> {
    try {
      const response = await api.post<ReponseProduit>('/products', donnees);
      return response.data.data;
    } catch (erreur) {
      console.error('Erreur lors de la création du produit:', erreur);
      throw new Error('Impossible de créer le produit. Vérifiez les données saisies.');
    }
  }

  /**
   * Met à jour un produit existant
   */
  async mettreAJourProduit(id: number, donnees: MiseAJourProduit): Promise<Produit> {
    try {
      const response = await api.put<ReponseProduit>(`/products/${id}`, donnees);
      return response.data.data;
    } catch (erreur) {
      console.error(`Erreur lors de la mise à jour du produit ${id}:`, erreur);
      throw new Error('Impossible de mettre à jour le produit.');
    }
  }

  /**
   * Supprime un produit
   */
  async supprimerProduit(id: number): Promise<void> {
    try {
      await api.delete(`/products/${id}`);
    } catch (erreur) {
      console.error(`Erreur lors de la suppression du produit ${id}:`, erreur);
      throw new Error('Impossible de supprimer le produit.');
    }
  }

  /**
   * Met à jour le stock d'un produit
   */
  async mettreAJourStock(id: number, quantite: number): Promise<Produit> {
    try {
      const response = await api.patch<ReponseProduit>(`/products/${id}/stock`, { quantite });
      return response.data.data;
    } catch (erreur) {
      console.error(`Erreur lors de la mise à jour du stock du produit ${id}:`, erreur);
      throw new Error('Impossible de mettre à jour le stock du produit.');
    }
  }

  /**
   * Récupère la liste des marques
   */
  async listerMarques(): Promise<Marque[]> {
    try {
      const response = await api.get<{ data: Marque[] }>('/api/brands');
      return response.data.data;
    } catch (erreur) {
      console.error('Erreur lors de la récupération des marques:', erreur);
      throw new Error('Impossible de charger la liste des marques.');
    }
  }

  /**
   * Active ou désactive le statut global de tous les produits (Mode maintenance)
   */
  async basculerStatutGlobalProduits(actif: boolean): Promise<{ success: boolean; message: string; actif: boolean; shop_maintenance: boolean }> {
    try {
      const response = await api.post('/products/toggle-all-status', { actif });
      return response.data;
    } catch (erreur) {
      console.error('Erreur lors du basculement global des produits:', erreur);
      throw new Error('Impossible de modifier le statut de la boutique.');
    }
  }

  /**
   * Obtenir le statut de maintenance de la boutique
   */
  async obtenirStatutMaintenance(): Promise<{ shop_maintenance: boolean; active_products_count: number }> {
    try {
      const response = await api.get('/products/maintenance-mode');
      return response.data;
    } catch (erreur) {
      return { shop_maintenance: false, active_products_count: 0 };
    }
  }
}

export const serviceProduits = new ServiceProduits();